import { ref, onMounted, onUnmounted, getCurrentInstance, Ref } from 'vue'

// Generic event data type - can be any JSON-serializable data
export type EventData = Record<string, unknown> | unknown

export interface LiveUpdateOptions {
  autoReconnect?: boolean
  reconnectDelay?: number
  maxReconnectAttempts?: number
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Error | unknown) => void
}

export interface UpdateSubscription {
  event: string
  handler: (data: EventData) => void
  priority?: 'high' | 'medium' | 'low'
}

interface PendingUpdate {
  event: string
  data: EventData
  timestamp: number
  priority: 'high' | 'medium' | 'low'
}

export function useLiveUpdates(options: LiveUpdateOptions = {}) {
  const {
    autoReconnect = true,
    reconnectDelay = 5000,
    maxReconnectAttempts = 10,
    onConnect,
    onDisconnect,
    onError
  } = options

  // State
  const subscriptions = ref<Map<string, Set<(data: EventData) => void>>>(new Map())
  const eventListeners = ref<Map<string, EventListener>>(new Map())
  const pendingUpdates = ref<PendingUpdate[]>([])
  const updateQueue = ref<Map<string, EventData>>(new Map())
  const reconnectAttempts = ref(0)
  const isProcessing = ref(false)

  // Animation frame ID for batched updates
  let animationFrameId: number | null = null

  // Subscribe to an event
  const subscribe = (event: string, handler: (data: EventData) => void, priority: 'high' | 'medium' | 'low' = 'medium') => {
    // Add to subscriptions map
    if (!subscriptions.value.has(event)) {
      subscriptions.value.set(event, new Set())
    }
    subscriptions.value.get(event)?.add(handler)

    // Create event listener if not exists
    if (!eventListeners.value.has(event)) {
      const listener = (e: Event) => {
        const customEvent = e as CustomEvent
        handleUpdate(event, customEvent.detail, priority)
      }
      eventListeners.value.set(event, listener)
      window.addEventListener(event, listener)
    }

    console.log(`Subscribed to ${event} with priority ${priority}`)
  }

  // Unsubscribe from an event
  const unsubscribe = (event: string, handler?: (data: EventData) => void) => {
    const handlers = subscriptions.value.get(event)

    if (handlers) {
      if (handler) {
        handlers.delete(handler)
      } else {
        // Remove all handlers for this event
        handlers.clear()
      }

      // Remove event listener if no more handlers
      if (handlers.size === 0) {
        const listener = eventListeners.value.get(event)
        if (listener) {
          window.removeEventListener(event, listener)
          eventListeners.value.delete(event)
        }
        subscriptions.value.delete(event)
      }
    }
  }

  // Handle incoming update
  const handleUpdate = (event: string, data: EventData, priority: 'high' | 'medium' | 'low') => {
    // Add to pending updates
    pendingUpdates.value.push({
      event,
      data,
      timestamp: Date.now(),
      priority
    })

    // Process high priority updates immediately
    if (priority === 'high') {
      processUpdates()
    } else {
      // Batch medium and low priority updates
      scheduleUpdateProcessing()
    }
  }

  // Schedule update processing using requestAnimationFrame
  const scheduleUpdateProcessing = () => {
    if (animationFrameId === null && !isProcessing.value) {
      animationFrameId = requestAnimationFrame(() => {
        processUpdates()
        animationFrameId = null
      })
    }
  }

  // Process pending updates
  const processUpdates = () => {
    if (isProcessing.value || pendingUpdates.value.length === 0) {
      return
    }

    isProcessing.value = true

    // Sort by priority and timestamp
    const sorted = pendingUpdates.value.sort((a, b) => {
      const priorityOrder = { high: 1, medium: 2, low: 3 }
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority]
      }
      return a.timestamp - b.timestamp
    })

    // Process updates
    const processed = new Set<string>()
    for (const update of sorted) {
      // Coalesce updates of the same type
      const key = `${update.event}:${JSON.stringify(update.data.id || update.data)}`
      if (!processed.has(key)) {
        processed.add(key)

        // Call all handlers for this event
        const handlers = subscriptions.value.get(update.event)
        if (handlers) {
          handlers.forEach(handler => {
            try {
              handler(update.data)
            } catch (error) {
              console.error(`Error in handler for ${update.event}:`, error)
              onError?.(error)
            }
          })
        }
      }
    }

    // Clear processed updates
    pendingUpdates.value = []
    isProcessing.value = false
  }

  // Subscribe to all updates for a component
  const subscribeAll = (subscriptions: UpdateSubscription[]) => {
    subscriptions.forEach(sub => {
      subscribe(sub.event, sub.handler, sub.priority)
    })
  }

  // Unsubscribe from all updates
  const unsubscribeAll = () => {
    // Remove all event listeners
    eventListeners.value.forEach((listener, event) => {
      window.removeEventListener(event, listener)
    })

    // Clear all maps
    subscriptions.value.clear()
    eventListeners.value.clear()
    pendingUpdates.value = []
    updateQueue.value.clear()

    // Cancel any pending animation frame
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }
  }

  // Get update statistics
  const getStats = () => {
    const stats = {
      subscriptions: subscriptions.value.size,
      pendingUpdates: pendingUpdates.value.length,
      handlers: Array.from(subscriptions.value.values()).reduce(
        (sum, handlers) => sum + handlers.size,
        0
      )
    }
    return stats
  }

  // Cleanup on unmount (only if called from a component context)
  const instance = getCurrentInstance()
  if (instance) {
    onUnmounted(() => {
      unsubscribeAll()
    })
  }
  // If not in component context (e.g., called from LiveUpdateManager service),
  // cleanup will be handled by the service itself

  return {
    subscribe,
    unsubscribe,
    subscribeAll,
    unsubscribeAll,
    processUpdates,
    getStats,
    pendingUpdates,
    isProcessing
  }
}

// Create a global instance for shared subscriptions
let globalInstance: ReturnType<typeof useLiveUpdates> | null = null

export function useGlobalLiveUpdates(options?: LiveUpdateOptions) {
  if (!globalInstance) {
    globalInstance = useLiveUpdates(options)
  }
  return globalInstance
}